import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Users, User, Globe, Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Wordmark, scrollToId, scrollToMenu } from "@/components/brand/BrandAssets";

const T = {
  menu: { lv: "Ēdienkarte", ru: "Меню", en: "Menu" },
  about: { lv: "Par mums", ru: "О нас", en: "About" },
  delivery: { lv: "Piegāde", ru: "Доставка", en: "Delivery" },
  order: { lv: "Pasūtīt", ru: "Заказать", en: "Order" },
  groupOrder: { lv: "Grupas pasūtījums", ru: "Групповой заказ", en: "Group order" },
  login: { lv: "Pieslēgties", ru: "Войти", en: "Log in" },
  myAccount: { lv: "Mans konts", ru: "Мой аккаунт", en: "My account" },
  logout: { lv: "Iziet", ru: "Выйти", en: "Log out" },
  cart: { lv: "Grozs", ru: "Корзина", en: "Cart" },
  language: { lv: "Valoda", ru: "Язык", en: "Language" },
  more: { lv: "Izvēlne", ru: "Меню", en: "Menu" },
};

const BrandNav = ({ language, setLanguage, cartItemsCount, onCartClick, onGroupOrderClick }) => {
  const { user, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const t = (k) => T[k][language] || T[k].lv;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const overlay = !scrolled;
  const shell = overlay
    ? "rz-nav--overlay text-[#FDFBF7] bg-transparent border-transparent"
    : "text-[#1C1917] bg-[#FDFBF7]/90 backdrop-blur-md border-[#E7E5E4]";
  const link = "rz-link hidden lg:inline-flex items-center min-h-[44px] text-[0.72rem] font-medium tracking-[0.22em] uppercase";

  return (
    <header className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-500 ${shell}`} data-testid="brand-nav" data-scrolled={scrolled}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[68px] sm:h-[76px] flex items-center justify-between gap-4">
        <a
          href="#top"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="inline-flex items-center min-h-[44px]"
          aria-label="Roze di Napoli"
          data-testid="nav-wordmark"
        >
          <Wordmark size="nav" tone={overlay ? "light" : "dark"} className="hidden min-[400px]:block" />
        </a>

        <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
          <a href="#menu-section" onClick={(e) => { e.preventDefault(); scrollToMenu(); }} className={link} data-testid="hero-nav-menu">{t("menu")}</a>
          <a href="#about-section" onClick={(e) => { e.preventDefault(); scrollToId("about-section"); }} className={link} data-testid="nav-about">{t("about")}</a>
          <a href="#delivery-section" onClick={(e) => { e.preventDefault(); scrollToId("delivery-section"); }} className={link} data-testid="nav-delivery">{t("delivery")}</a>
        </nav>

        <div className="flex items-center gap-0 sm:gap-2" data-testid="hero-utilities">
          {/* Mobile: menu link + cart + "more" menu */}
          <a
            href="#menu-section"
            onClick={(e) => { e.preventDefault(); scrollToMenu(); }}
            className="lg:hidden rz-link inline-flex items-center min-h-[44px] px-2 text-[0.68rem] font-medium tracking-[0.18em] uppercase"
            data-testid="mobile-nav-menu"
          >
            {t("menu")}
          </a>

          {/* Desktop utilities */}
          <div className="hidden lg:flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rz-icon-btn gap-1.5 text-[0.72rem] font-medium tracking-[0.18em] uppercase" aria-label={t("language")} data-testid="language-selector">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span>{language}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="language-dropdown">
                <DropdownMenuItem onClick={() => setLanguage("lv")} data-testid="lang-lv">Latviešu</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("ru")} data-testid="lang-ru">Русский</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("en")} data-testid="lang-en">English</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button onClick={onGroupOrderClick} className="rz-icon-btn" aria-label={t("groupOrder")} title={t("groupOrder")} data-testid="group-order-button">
              <Users className="w-[18px] h-[18px]" strokeWidth={1.5} />
            </button>

            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rz-icon-btn" aria-label={t("myAccount")} title={user?.full_name || t("myAccount")} data-testid="account-button">
                    <User className="w-[18px] h-[18px]" strokeWidth={1.5} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="account-dropdown">
                  <DropdownMenuItem onClick={() => navigate("/my-account")} data-testid="my-account-link">{t("myAccount")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="logout-button">{t("logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button onClick={() => navigate("/login")} className="rz-icon-btn" aria-label={t("login")} title={t("login")} data-testid="login-button">
                <User className="w-[18px] h-[18px]" strokeWidth={1.5} />
              </button>
            )}
          </div>

          <button onClick={onCartClick} className="rz-icon-btn relative" aria-label={`${t("cart")} (${cartItemsCount})`} data-testid="cart-button">
            <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.5} />
            {cartItemsCount > 0 && (
              <span className={`absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[0.65rem] font-semibold flex items-center justify-center ${overlay ? "bg-[#FDFBF7] text-[#1C1917]" : "bg-[#1C1917] text-[#FDFBF7]"}`} data-testid="cart-count">
                {cartItemsCount}
              </span>
            )}
          </button>

          <button onClick={onCartClick} className={`rz-btn rz-btn--sm ml-1 sm:ml-3 ${overlay ? "rz-btn--light" : ""}`} data-testid="hero-nav-order">
            {t("order")}
          </button>

          {/* Mobile "more" menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="lg:hidden rz-icon-btn ml-1" aria-label={t("more")} data-testid="mobile-more-button">
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 rounded-none border-[#E7E5E4] bg-[#FDFBF7] p-2 font-body" data-testid="mobile-more-menu">
              <DropdownMenuLabel className="rz-eyebrow !text-[0.62rem] px-2 py-2">{t("language")}</DropdownMenuLabel>
              <div className="flex gap-1 px-2 pb-2">
                {[["lv", "LV"], ["ru", "RU"], ["en", "EN"]].map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => setLanguage(code)}
                    aria-pressed={language === code}
                    className={`flex-1 min-h-[40px] text-[0.7rem] font-medium tracking-[0.16em] border transition-colors ${language === code ? "bg-[#1C1917] text-[#FDFBF7] border-[#1C1917]" : "border-[#E7E5E4] text-[#57534E]"}`}
                    data-testid={`mobile-lang-${code}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <DropdownMenuSeparator className="bg-[#E7E5E4]" />
              <DropdownMenuItem onClick={() => scrollToId("about-section")} className="min-h-[44px] text-[0.8rem] tracking-[0.12em] uppercase" data-testid="mobile-nav-about">{t("about")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => scrollToId("delivery-section")} className="min-h-[44px] text-[0.8rem] tracking-[0.12em] uppercase" data-testid="mobile-nav-delivery">{t("delivery")}</DropdownMenuItem>
              <DropdownMenuItem onClick={onGroupOrderClick} className="min-h-[44px] text-[0.8rem] tracking-[0.12em] uppercase" data-testid="mobile-group-order">
                <Users className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("groupOrder")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#E7E5E4]" />
              {isLoggedIn ? (
                <>
                  <DropdownMenuItem onClick={() => navigate("/my-account")} className="min-h-[44px] text-[0.8rem] tracking-[0.12em] uppercase" data-testid="mobile-my-account">
                    <User className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("myAccount")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="min-h-[44px] text-[0.8rem] tracking-[0.12em] uppercase" data-testid="mobile-logout">{t("logout")}</DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => navigate("/login")} className="min-h-[44px] text-[0.8rem] tracking-[0.12em] uppercase" data-testid="mobile-login">
                  <User className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("login")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default BrandNav;
